import { FadeIn } from "./common";
import CategoryHeader from "./CategoryHeader";
import { CategoryBackground } from "./SciBackground";

const EXPERTISE = [
  {
    title: "Product Quality & Quality Systems",
    items: ["Product Quality", "QMS", "RCA", "CAPA", "Deviation Management", "Nonconformance Investigation", "Atypical Result Investigation", "Change Control", "ECO Support", "Specification Lifecycle", "Acceptance-Criteria Assessment", "Audit Readiness", "Data Integrity", "ALCOA+", "GDP"],
  },
  {
    title: "Technical Documentation",
    items: ["SOP Drafting", "SOP Revision", "Work Instructions", "Technical Reports", "Deviation Documentation", "CAPA Documentation", "ECO / Change Documentation", "Validation Documentation", "Controlled Forms", "Revision Control", "Document Lifecycle", "Training Impact Assessment"],
  },
  {
    title: "Regulated Manufacturing",
    items: ["cGMP", "FDA-Regulated Manufacturing Principles", "Product Quality Operations", "Process Troubleshooting", "Yield Improvement", "Process Monitoring", "Equipment Status / Calibration Awareness", "Material Genealogy", "Preventive Controls", "Quality Systems"],
  },
  {
    title: "Quality Risk Management",
    items: ["Risk Identification", "Risk Assessment", "Severity / Occurrence / Detectability", "FMEA", "Risk Controls", "Residual Risk", "Risk-Based Decision Making", "Risk Review"],
  },
  {
    title: "Data & Automation",
    items: ["Python", "pandas", "REST APIs", "Data Pipelines", "Schema Validation", "Data Normalization", "Data Reconciliation", "Data Lineage", "Source Traceability", "Deterministic Processing", "Checkpoint / Resume Logic", "Failure Recovery", "Scheduled Automation"],
  },
  {
    title: "Manufacturing Analytics",
    items: ["JMP", "Statistical Analysis", "Statistical Process Control Concepts", "Trend Detection", "Distribution Analysis", "Cp/Cpk Concepts", "3σ Analysis", "FPY", "Final Yield", "Retest Recovery", "Cost of Poor Quality", "Failure-Mode Trending", "Signal Analytics"],
  },
  {
    title: "Continuous Improvement",
    items: ["Operational Excellence", "Yield Recovery", "Process Optimization", "Variation Reduction", "Failure Recurrence Reduction", "Effectiveness Monitoring", "KPI Governance", "Performance Baseline", "PDCA", "DMAIC-style Problem Solving", "Process Mapping", "Standard Work Concepts"],
  },
  {
    title: "Digital / Systems",
    items: ["Dashboard Design", "Retool", "API Integration", "IoT", "Sensor Data", "Requirements Definition", "Data Architecture", "UAT Concepts", "CSV Concepts", "GAMP 5 Working Knowledge", "21 CFR Part 11 Awareness"],
  },
];

const MATRIX = [
  { col: "Product Quality", color: "#137C8B", items: ["RCA", "CAPA", "Failure Trending", "FPY", "Specification Lifecycle"] },
  { col: "QMS", color: "#7C6FE5", items: ["Deviation", "ECO", "Change Control", "SOP / WI", "Document Lifecycle"] },
  { col: "Manufacturing", color: "#2D7FF9", items: ["Process Control", "Variation Reduction", "Yield", "Equipment Performance", "Lot Genealogy"] },
  { col: "Automation", color: "#20B8CD", items: ["Python", "REST APIs", "Scheduled Automation", "Data Pipelines", "IoT"] },
  { col: "Data", color: "#8A99A8", items: ["JMP", "Power BI", "pandas", "Time-Series Analysis", "Data Reconciliation"] },
  { col: "Compliance", color: "#35B779", items: ["cGMP", "GDP", "ALCOA+", "Data Integrity", "FDA / QMSR awareness", "ISO 13485 awareness"] },
];

const LEGEND = [
  ["Product Quality", "#137C8B"],
  ["QMS / Controlled Documentation", "#7C6FE5"],
  ["Automation / Digital Manufacturing", "#20B8CD"],
  ["Manufacturing / Analytics", "#2D7FF9"],
  ["Operational Excellence", "#35B779"],
  ["RCA / Investigation", "#E9A23B"],
  ["Deviation / Failure", "#D85858"],
  ["Data & Compliance", "#8A99A8"],
];

const STACK = [
  ["Analysis", ["Python", "pandas", "JMP"]],
  ["Integration", ["REST APIs", "JSON", "CSV", "File Systems", "Sensor APIs"]],
  ["Automation", ["Python scripting", "Scheduled workflows", "Windows Task Scheduler", "Data harvesters", "Validation logic"]],
  ["Visualization", ["Interactive dashboards", "Retool concepts", "Scientific charting", "Pareto", "Trend visualization"]],
  ["Quality", ["GMP", "CAPA", "Change Control", "Data Integrity", "ALCOA+"]],
];

const TIMELINE = [
  ["Hands-On Biotechnology / Product Quality", "Understanding analytical systems and manufacturing processes from the bench."],
  ["Quality Systems", "RCA, CAPA, deviations, documentation, risk and controlled change."],
  ["Manufacturing Analytics", "Using process data to understand yield and recurring failures."],
  ["Automation", "Building Python harvesters and repeatable analytical workflows."],
  ["IoT & Data Integration", "Connecting sensor, manufacturing and system data."],
  ["Digital Manufacturing", "Designing integrated dashboards, automated decision-support systems and scalable data workflows."],
];

export default function Expertise() {
  return (
    <section id="expertise" className="relative overflow-hidden scroll-mt-16" data-testid="expertise-section">
      <CategoryBackground variant="lattice" accent="teal" accent2="violet" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
      <CategoryHeader
        id="expertise"
        category="expertise"
        eyebrow="Expertise"
        title="Capability by domain — no invented percentages"
        description="A cross-functional capability matrix across Product Quality, QMS, Automation, Analytics, Compliance and Operational Excellence — stated categorically and honestly: what has been applied, in which context, at what depth."
      />
      <div className="mt-10" data-testid="expertise-matrix">
        <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4" data-testid="domain-legend">
          {LEGEND.map(([label, color]) => (
            <span key={label} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-steel">
              <span className="w-2 h-2 rounded-sm inline-block" style={{ background: color }} aria-hidden="true" />
              {label}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border border-cyan/15 rounded-md overflow-hidden">
          {MATRIX.map((m, i) => (
            <div key={m.col} className={`p-4 bg-navy2 ${i < MATRIX.length - 1 ? "border-b md:border-b-0 lg:border-r border-cyan/15" : ""}`} data-testid={`matrix-${m.col.toLowerCase()}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em]" style={{ color: m.color }}>
                {m.col}
              </p>
              <div className="h-0.5 w-6 mt-2 mb-3" style={{ background: m.color }} aria-hidden="true" />
              <ul className="space-y-1.5">
                {m.items.map((item) => (
                  <li key={item} className="text-[11px] text-pale/85 font-body">{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
        {EXPERTISE.map((cat, i) => (
          <FadeIn key={cat.title} delay={(i % 3) * 0.06}>
            <div className="panel p-5 h-full" data-testid={`expertise-${cat.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <p className="overline-label">{cat.title}</p>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {cat.items.map((item) => (
                  <span key={item} className="font-mono text-[10px] px-2 py-1 rounded-sm bg-navy border border-teal/30 text-pale/80">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </FadeIn>
        ))}
      </div>

      <div className="mt-16">
        <h3 className="font-sans tracking-tight text-2xl text-paper">Technology Ecosystem</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 mt-8 border border-cyan/15 rounded-md overflow-hidden">
          {STACK.map(([group, items], i) => (
            <div key={group} className={`p-5 bg-navy2 ${i < STACK.length - 1 ? "border-b sm:border-b-0 lg:border-r border-cyan/15" : ""}`} data-testid={`stack-${group.toLowerCase()}`}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan">{group}</p>
              <ul className="mt-3 space-y-1.5">
                {items.map((item) => (
                  <li key={item} className="text-xs text-pale/80 font-body">{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-16">
        <h3 className="font-sans tracking-tight text-2xl text-paper">Professional Development Story</h3>
        <p className="text-steel text-sm mt-2">A deliberate progression — from the bench to digital manufacturing.</p>
        <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-0 mt-8 border border-cyan/15 rounded-md overflow-hidden" data-testid="career-timeline">
          {TIMELINE.map(([t, d], i) => (
            <FadeIn key={t} delay={i * 0.07}>
              <div className="relative h-full p-5 bg-navy2 border-b lg:border-b-0 lg:border-r last:border-r-0 border-cyan/15" data-testid={`timeline-stage-${i + 1}`}>
                <p className="font-mono text-[10px] text-teal">STAGE {i + 1}</p>
                <p className="font-sans text-sm font-semibold text-paper mt-2 leading-snug">{t}</p>
                <p className="text-[11px] text-steel mt-2 leading-snug">{d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
      </div>
    </section>
  );
}
